import { useEffect } from 'react';

export type PageMeta = {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  jsonLd?: object;
};

type Restore = () => void;

/**
 * Sets the head tags of the current page. The site has no head manager, so the
 * tags shipped in public/index.html are updated in place and restored when the
 * page unmounts, which keeps client side navigation between pages consistent.
 * The prerenderer snapshots the DOM after effects have run, so the static HTML
 * of every prerendered route already carries the right tags.
 */
const applyMeta = (selector: string, create: () => HTMLMetaElement, content: string): Restore => {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) {
    const previous = existing.getAttribute('content');
    existing.setAttribute('content', content);
    return () => (previous === null ? existing.removeAttribute('content') : existing.setAttribute('content', previous));
  }

  const created = create();
  created.setAttribute('content', content);
  document.head.appendChild(created);
  return () => created.remove();
};

const applyNamedMeta = (name: string, content: string): Restore =>
  applyMeta(
    `meta[name="${name}"]`,
    () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      return meta;
    },
    content,
  );

const applyPropertyMeta = (property: string, content: string): Restore =>
  applyMeta(
    `meta[property="${property}"]`,
    () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      return meta;
    },
    content,
  );

const applyCanonical = (href: string): Restore => {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (existing) {
    const previous = existing.getAttribute('href');
    existing.setAttribute('href', href);
    return () => (previous === null ? existing.removeAttribute('href') : existing.setAttribute('href', previous));
  }

  const created = document.createElement('link');
  created.setAttribute('rel', 'canonical');
  created.setAttribute('href', href);
  document.head.appendChild(created);
  return () => created.remove();
};

const applyJsonLd = (jsonLd: string): Restore => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.pageJsonLd = 'true';
  script.textContent = jsonLd;
  document.head.appendChild(script);
  return () => script.remove();
};

export const usePageMeta = (meta: PageMeta): void => {
  const { title, description, keywords, canonical, ogTitle, ogDescription, ogUrl } = meta;
  const jsonLd = meta.jsonLd ? JSON.stringify(meta.jsonLd) : undefined;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const restores: Restore[] = [];
    if (description) {
      restores.push(applyNamedMeta('description', description));
      restores.push(applyNamedMeta('twitter:description', ogDescription ?? description));
    }
    if (keywords) {
      restores.push(applyNamedMeta('keywords', keywords));
    }
    if (ogDescription) {
      restores.push(applyPropertyMeta('og:description', ogDescription));
    }
    if (ogTitle) {
      restores.push(applyPropertyMeta('og:title', ogTitle));
      restores.push(applyNamedMeta('twitter:title', ogTitle));
      restores.push(applyNamedMeta('twitter:card', 'summary_large_image'));
    }
    if (ogUrl) {
      restores.push(applyPropertyMeta('og:url', ogUrl));
    }
    if (canonical) {
      restores.push(applyCanonical(canonical));
    }
    if (jsonLd) {
      restores.push(applyJsonLd(jsonLd));
    }

    return () => {
      document.title = previousTitle;
      restores.reverse().forEach((restore) => restore());
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogUrl, jsonLd]);
};
